import {getStepsFromPixels} from 'platform/foundation';

import {RefObject, useLayoutEffect, useState} from 'react';

import {isNil} from 'ramda';
import {noop} from 'ramda-adjunct';

/**
 * Whether the referenced element is narrower than `minWidth` (in theme spacing steps), re-evaluated
 * whenever the element resizes - e.g. while the user drags a splitter that narrows its panel.
 *
 * Deliberately not built on `useElementSize`: that hook keeps the pixel size in state, so every
 * frame of a drag would re-render the consumer - here the whole sourcing list. This state is a
 * boolean, so React bails out of re-rendering until the answer actually flips between "fits" and
 * "too narrow".
 *
 * The first measurement runs before paint, so a panel that is already too narrow on mount renders
 * the cards right away instead of flashing the compact table first (same pattern as `useIsTruncated`
 * in `shared/web`).
 *
 * A measured width of 0 is skipped and the previous answer is kept: it means the element is not laid
 * out yet or is hidden (`display: none`, a collapsed panel), not that it is genuinely too narrow.
 *
 * @param elementRef A ref attached to the element being measured
 * @param minWidth The narrowest width, in theme spacing steps, at which the content still fits
 */
export function useIsElementNarrowerThan(
  elementRef: RefObject<HTMLElement | null>,
  minWidth: number
) {
  const [isNarrower, setIsNarrower] = useState(false);

  useLayoutEffect(
    function observeElementWidth() {
      const element = elementRef.current;

      if (isNil(element)) {
        return noop;
      }

      const updateIsNarrower = () => {
        if (element.clientWidth === 0) {
          return;
        }

        const widthInSteps = getStepsFromPixels(element.clientWidth) ?? 0;
        setIsNarrower(widthInSteps < minWidth);
      };

      updateIsNarrower();

      const resizeObserver = new ResizeObserver(updateIsNarrower);
      resizeObserver.observe(element);

      return () => resizeObserver.disconnect();
    },
    [elementRef, minWidth]
  );

  return isNarrower;
}
